/**
 * Wedding-website pitch surfaces inside the RSVP builder.
 *
 * Two placements, one product, one dismissal:
 *   1. A one-line banner at the top of Step 1 (Create) — #weddingSitesBanner
 *   2. A short strip under the share card on Step 3   — #weddingSitePitchShare
 *
 * The two carry deliberately different weight. The banner interrupts a host who
 * has not typed anything yet, so it is a headline, three thumbnails and a link —
 * roughly the height of the pill it replaced. The Share strip catches a host who
 * has a link in front of them and is deciding what to send, which is the last
 * honest moment to offer a website, so it carries the actual argument.
 *
 * The Share strip sits between the share card and the hand-off to the
 * dashboard (since 2026-10-15), so it is on screen while the host still has
 * the link in hand. (Earlier, below two sharing cards, it fell off the bottom
 * of a 1080p laptop viewport entirely.)
 *
 * Why this file exists at all: the banner used to be ~30 lines of inline markup
 * plus an inline <script>, duplicated across seven builder HTML files. Every
 * copy drifted (the Signature line was hardcoded English in all six localised
 * builders, so Hebrew and Arabic Signature hosts read "Included with Signature"
 * in English). One module, one string table, seven one-line placeholders.
 *
 * Three deliberate choices, all reversals of how the banner used to work:
 *
 * - **It links to the landing page, not the wizard.** The six openable sample
 *   sites are the actual argument for the product, and the wizard never shows
 *   them. This matches the homepage feature card, which points at
 *   /wedding-websites for the same reason.
 * - **No price on the non-Signature copy.** The landing page prices it properly
 *   ("Free with Signature. Thirty dollars on its own." plus the 2-year/$5-a-year
 *   fine print) and the banner cannot say that accurately in eight words — a
 *   truncated price is worse than none. Hosts with an unclaimed free site get
 *   a line instead, because for them it is not a price, it is a waiting benefit.
 * - **It shows the designs rather than claiming they are nice.** Three real
 *   captures of the live sample sites, the same ones the landing page uses.
 *
 * Both surfaces share `WSP_DISMISS_KEY`, so one × silences the product rather
 * than moving the nag to the next step.
 *
 * The public API is unchanged from the inline era — auth-manager.js still calls
 * window._maybeShowWeddingSitesBanner(hasFreeSiteClaim) once the server has told
 * it whether a free site is claimable, and bails if the global is missing. So
 * this file must be loaded as a classic script before auth state resolves; it
 * sits with the other builder scripts, ahead of auth-manager.js itself.
 *
 * Not loaded on org-builder.html or ticket-builder.html: a corporate offsite
 * and a ticketed conference have no use for a wedding website, and the banner
 * used to show on both.
 */

(function () {
    'use strict';

    var WSP_DISMISS_KEY = 'quikrsvp_wedding_sites_banner_dismissed';

    // Real captures of the live sample sites (scripts/capture-wedding-landing-shots.js).
    // Three, not six: enough to show the range without turning a banner into a
    // gallery. Blush / green / near-black reads as three different designs at
    // thumbnail size, which two adjacent pale templates would not.
    var SHOTS = [
        { slug: 'romantic-blush', name: 'Romantic Blush' },
        { slug: 'garden-party', name: 'Garden Party' },
        { slug: 'classic-elegance', name: 'Classic Elegance' }
    ];
    // Party sample sites, the same 390x844 phone capture as the wedding shots
    // (of /wedding-websites/samples/<slug>), for an event the event-category
    // module reads as a birthday, quinceañera, mitzvah or party.
    var PARTY_SHOTS = [
        { slug: 'fiesta', name: 'Fiesta' },
        { slug: 'graffiti', name: 'Graffiti' },
        { slug: 'retro-pop', name: 'Retro Pop' }
    ];
    var SHOT_VERSION = 1;

    var COPY = {
        en: {
            headline: 'Your RSVP can live inside a beautiful wedding website',
            cta: 'See the designs →',
            shareTitle: 'Give them more than a form',
            shareBody: 'The same RSVP, built into a full wedding website: your story, the schedule, directions, photos and a gift registry. One address to send instead of a form link.',
            freeClaim: 'You have a free wedding site to claim.',
            dismiss: 'Dismiss'
        },
        es: {
            headline: 'Tu RSVP puede vivir dentro de una preciosa web de boda',
            cta: 'Ver los diseños →',
            shareTitle: 'Dales algo más que un formulario',
            shareBody: 'El mismo RSVP, dentro de una web de boda completa: vuestra historia, el programa, cómo llegar, fotos y una lista de regalos. Una sola dirección para enviar, en lugar de un enlace a un formulario.',
            freeClaim: 'Tienes una web de boda gratis por reclamar.',
            dismiss: 'Cerrar'
        },
        fr: {
            headline: 'Votre RSVP peut vivre dans un magnifique site de mariage',
            cta: 'Voir les designs →',
            shareTitle: 'Offrez-leur plus qu’un formulaire',
            shareBody: 'Le même RSVP, intégré à un vrai site de mariage : votre histoire, le programme, l’itinéraire, les photos et une liste de mariage. Une seule adresse à envoyer, plutôt qu’un lien vers un formulaire.',
            freeClaim: 'Vous avez un site de mariage gratuit à réclamer.',
            dismiss: 'Fermer'
        },
        de: {
            headline: 'Ihr RSVP-Formular kann Teil einer wunderschönen Hochzeits-Website sein',
            cta: 'Designs ansehen →',
            shareTitle: 'Mehr als nur ein Formular',
            shareBody: 'Dasselbe RSVP, eingebettet in eine komplette Hochzeits-Website: Ihre Geschichte, der Ablauf, die Anfahrt, Fotos und eine Geschenkeliste. Eine Adresse zum Verschicken statt eines Formularlinks.',
            freeClaim: 'Sie haben eine kostenlose Hochzeits-Website zum Einlösen.',
            dismiss: 'Schließen'
        },
        pt: {
            headline: 'O seu RSVP pode viver dentro de um lindo site de casamento',
            cta: 'Ver os designs →',
            shareTitle: 'Dê a eles mais do que um formulário',
            shareBody: 'O mesmo RSVP, dentro de um site de casamento completo: a sua história, o programa, como chegar, fotos e uma lista de presentes. Um endereço para enviar, em vez de um link de formulário.',
            freeClaim: 'Você tem um site de casamento grátis para resgatar.',
            dismiss: 'Fechar'
        },
        he: {
            headline: 'טופס אישור ההגעה שלכם יכול לחיות בתוך אתר חתונה מהמם',
            cta: 'לצפייה בעיצובים ←',
            shareTitle: 'תנו להם יותר מטופס',
            shareBody: 'אותו אישור הגעה, בתוך אתר חתונה מלא: הסיפור שלכם, לוח הזמנים, הוראות הגעה, תמונות ורשימת מתנות. כתובת אחת לשלוח, במקום קישור לטופס.',
            freeClaim: 'יש לכם אתר חתונה חינם לממש.',
            dismiss: 'סגירה'
        },
        ar: {
            headline: 'يمكن لنموذج تأكيد الحضور أن يعيش داخل موقع زفاف جميل',
            cta: 'شاهد التصاميم ←',
            shareTitle: 'امنح ضيوفك أكثر من نموذج',
            shareBody: 'نفس نموذج تأكيد الحضور، مدمجًا داخل موقع زفاف كامل: قصتكما، البرنامج، الاتجاهات، الصور وقائمة الهدايا. عنوان واحد ترسله بدلًا من رابط نموذج.',
            freeClaim: 'لديك موقع زفاف مجاني بانتظار المطالبة به.',
            dismiss: 'إغلاق'
        }
    };

    // The same two surfaces for a party (2026-11-03). Party templates now start
    // with party wording (guest of honor, one name), so a birthday host is no
    // longer pitched "your story" and a wedding registry.
    var PARTY_COPY = {
        en: {
            headline: 'Your RSVP can live inside a fun party website',
            shareBody: 'The same RSVP, built into a party website: the details, directions, photos and a countdown. One address to send instead of a form link.',
            freeClaim: 'You have a free party site to claim.'
        },
        es: {
            headline: 'Tu RSVP puede vivir dentro de una web de fiesta divertida',
            shareBody: 'El mismo RSVP, dentro de una web para tu fiesta: los detalles, cómo llegar, fotos y una cuenta regresiva. Una sola dirección para enviar, en lugar de un enlace a un formulario.',
            freeClaim: 'Tienes una web de fiesta gratis por reclamar.'
        },
        fr: {
            headline: 'Votre RSVP peut vivre dans un site de fête plein de style',
            shareBody: 'Le même RSVP, intégré à un site pour votre fête : les infos pratiques, l’itinéraire, les photos et un compte à rebours. Une seule adresse à envoyer, plutôt qu’un lien vers un formulaire.',
            freeClaim: 'Vous avez un site de fête gratuit à réclamer.'
        },
        de: {
            headline: 'Ihr RSVP-Formular kann Teil einer schönen Party-Website sein',
            shareBody: 'Dasselbe RSVP, eingebettet in eine Party-Website: alle Infos, die Anfahrt, Fotos und ein Countdown. Eine Adresse zum Verschicken statt eines Formularlinks.',
            freeClaim: 'Sie haben eine kostenlose Party-Website zum Einlösen.'
        },
        pt: {
            headline: 'O seu RSVP pode viver dentro de um site de festa incrível',
            shareBody: 'O mesmo RSVP, dentro de um site para a sua festa: os detalhes, como chegar, fotos e uma contagem regressiva. Um endereço para enviar, em vez de um link de formulário.',
            freeClaim: 'Você tem um site de festa grátis para resgatar.'
        },
        he: {
            headline: 'טופס אישור ההגעה שלכם יכול לחיות בתוך אתר מהמם לאירוע',
            shareBody: 'אותו אישור הגעה, בתוך אתר לאירוע: כל הפרטים, הוראות הגעה, תמונות וספירה לאחור. כתובת אחת לשלוח, במקום קישור לטופס.',
            freeClaim: 'יש לכם אתר לאירוע חינם לממש.'
        },
        ar: {
            headline: 'يمكن لنموذج تأكيد الحضور أن يعيش داخل موقع جميل لحفلتك',
            shareBody: 'نفس نموذج تأكيد الحضور، مدمجًا داخل موقع لحفلتك: كل التفاصيل، الاتجاهات، الصور وعدّ تنازلي. عنوان واحد ترسله بدلًا من رابط نموذج.',
            freeClaim: 'لديك موقع مجاني لحفلتك بانتظار المطالبة به.'
        }
    };

    var lang = (document.documentElement.getAttribute('lang') || 'en').slice(0, 2).toLowerCase();
    if (!COPY[lang]) lang = 'en';
    var t = COPY[lang];

    // Each locale has its own landing page (ar/wedding-websites/, he/…), and the
    // samples on it open in that language. Sending a Hebrew host to the English
    // page would undercut the multilingual argument the page is making.
    var LANDING_URL = lang === 'en' ? '/wedding-websites' : '/' + lang + '/wedding-websites';
    // A party host goes to the party page's designs, where "Use this design"
    // opens the site builder on that template.
    var PARTY_URL = (lang === 'en' ? '/party' : '/' + lang + '/party') + '#designs';

    // 'wedding' | 'party' | 'none' (corporate: no site pitch) | null (unknown:
    // the wedding wording, as before), read from the event as typed so far.
    function siteKind() {
        try {
            var EC = window.EventCategory;
            var bc = window.builderCore;
            if (!EC || !bc) return null;
            var fd = bc.formData || {};
            var opts = bc.builderOptions || {};
            var f = Object.assign({}, fd, { title: opts.title || fd.title || '' });
            if (opts.occasion) f.occasion = opts.occasion;   // from a guide link
            return EC.siteKindOf(f);
        } catch (e) { return null; }
    }
    var kind = null;
    function copy(key) {
        return (kind === 'party' && PARTY_COPY[lang][key]) || t[key];
    }

    // True only when the server says a free site is claimable right now:
    // an eligible Signature/Event-Pro form AND an unspent claim.
    var canClaim = false;
    var rendered = false;

    function track(name, placement) {
        try {
            if (typeof gtag === 'function') {
                gtag('event', name, {
                    event_category: 'wedding_sites',
                    placement: placement,
                    tier: canClaim ? 'free_claim_available' : 'standard',
                    site_kind: kind || 'unknown',
                    language: lang
                });
            }
        } catch (e) { /* analytics must never break the builder */ }
    }

    function dismissed() {
        try {
            return !!localStorage.getItem(WSP_DISMISS_KEY);
        } catch (e) {
            return false;   // private mode / blocked storage: show it, don't crash
        }
    }

    function injectStyles() {
        if (document.getElementById('wsp-styles')) return;
        var css = [
            /* One line, roughly the height of the pill it replaced. This sits
               above Event Details before the host has typed anything, so it
               gets a headline, three thumbnails and nothing else — the feature
               list belongs on the Share strip and the landing page, not in an
               interruption. */
            '.wsp-banner{display:none;align-items:center;gap:0.7rem;',
            'background:linear-gradient(135deg,#FBF7F2,#F5EFE9);',
            'border:1px solid rgba(188,155,93,0.28);border-radius:999px;',
            'padding:0.3rem 0.9rem 0.3rem 0.4rem;font-size:0.85rem;color:#2C2C2C;}',
            '.wsp-banner.is-on{display:flex;}',

            '.wsp-thumbs{display:flex;gap:3px;flex-shrink:0;}',
            '.wsp-thumbs img{width:20px;height:43px;object-fit:cover;object-position:top center;',
            'border-radius:4px;border:1px solid rgba(18,18,18,0.10);',
            'box-shadow:0 1px 2px rgba(18,18,18,0.10);display:block;background:#EFEAE4;}',

            /* Wraps rather than truncating: at a typical canvas width the
               headline plus the Signature note overflows one line, and an
               ellipsis eats exactly the half worth reading. Two lines of 0.85rem
               still clear the 43px thumbnails, so the banner does not grow. */
            '.wsp-copy{flex:1;min-width:0;line-height:1.25;}',
            '.wsp-copy strong{font-weight:600;}',
            '.wsp-sub{color:#6B6B6B;}',

            /* Two classes deep, not one: builder-v2.css paints every link in
               the builder with `body.builder-v2 a`, which outranks a lone
               .wsp-cta and turned the gold call-to-action near-black. */
            '.wsp-banner .wsp-cta,.wsp-share .wsp-cta{white-space:nowrap;',
            'color:#8A6A28;font-weight:600;text-decoration:none;flex-shrink:0;}',
            '.wsp-banner .wsp-cta:hover,.wsp-share .wsp-cta:hover{',
            'color:#6E521C;text-decoration:underline;}',

            /* An 18px glyph is hard to hit on a phone: give it a 32px target
               and pull the margin in by the same amount so nothing moves. */
            '.wsp-x{background:none;border:none;cursor:pointer;color:#9A9A9A;',
            'font-size:1.2rem;line-height:1;padding:0;flex-shrink:0;',
            'display:inline-flex;align-items:center;justify-content:center;',
            'min-width:32px;min-height:32px;margin:-7px;}',
            '.wsp-x:hover{color:#6B6B6B;}',

            /* Share strip: a short row between the share card and the
               dashboard hand-off. Kept shallow so it does not push the
               hand-off below the fold. */
            '.wsp-share{display:none;align-items:center;gap:0.9rem;margin-bottom:1rem;',
            'background:#FFFFFF;border:1px solid rgba(18,18,18,0.08);border-radius:12px;',
            'padding:0.6rem 0.85rem;box-shadow:0 1px 3px rgba(18,18,18,0.04);}',
            '.wsp-share.is-on{display:flex;}',
            '.wsp-share-thumbs{display:flex;gap:4px;flex-shrink:0;}',
            '.wsp-share-thumbs img{width:26px;height:56px;object-fit:cover;object-position:top center;',
            'border-radius:5px;border:1px solid rgba(18,18,18,0.10);',
            'box-shadow:0 1px 3px rgba(18,18,18,0.10);display:block;background:#EFEAE4;}',
            '.wsp-share-copy{flex:1;min-width:0;}',
            '.wsp-share-copy h6{margin:0;font-size:0.88rem;font-weight:600;color:#2C2C2C;}',
            '.wsp-share-copy p{margin:0.1rem 0 0;font-size:0.8rem;line-height:1.4;color:#6B6B6B;}',
            '.wsp-owned{color:#1F7A4D;font-weight:600;}',

            /* Narrow: let the headline wrap instead of truncating to nothing,
               drop the pill radius (a two-line pill looks like a mistake), and
               lift the × into the corner — left in the row it sits marooned
               beside the thumbnails with the whole headline width as a gap. */
            '@media (max-width:640px){',
            '.wsp-banner,.wsp-share{position:relative;align-items:flex-start;',
            'padding-inline-end:2rem;}',
            '.wsp-banner{flex-wrap:wrap;border-radius:12px;padding:0.6rem 0.7rem;}',
            '.wsp-share{flex-wrap:wrap;}',
            /* basis 0, not auto: with `auto` the copy is measured at its full
               single-line width. In the banner that pushed it below the
               thumbnails (~50px of a 375px screen); in the share strip, which
               does not wrap its own row, it instead squeezed the paragraph into
               a one-word-wide column 450px tall. */
            '.wsp-copy,.wsp-share-copy{flex:1 1 0;}',
            '.wsp-banner .wsp-cta,.wsp-share .wsp-cta{flex:1 1 100%;}',
            '.wsp-banner .wsp-x,.wsp-share .wsp-x{position:absolute;top:0.4rem;',
            'inset-inline-end:0.5rem;}',
            '}'
        ].join('');
        var el = document.createElement('style');
        el.id = 'wsp-styles';
        el.textContent = css;
        document.head.appendChild(el);
    }

    function thumbsHtml(cls, w, h) {
        var out = '';
        var shots = kind === 'party' ? PARTY_SHOTS : SHOTS;
        for (var i = 0; i < shots.length; i++) {
            out += '<img src="/assets/images/wedding-landing/phone-' + shots[i].slug
                + '-sm-v' + SHOT_VERSION + '.webp" alt="' + shots[i].name
                + '" loading="lazy" decoding="async" width="' + w + '" height="' + h + '">';
        }
        return '<div class="' + cls + '">' + out + '</div>';
    }

    function ctaHtml(placement, cls) {
        return '<a class="' + cls + '" href="' + (kind === 'party' ? PARTY_URL : LANDING_URL) + '" target="_blank" rel="noopener"'
            + ' data-wsp-placement="' + placement + '">' + t.cta + '</a>';
    }

    function closeHtml() {
        return '<button type="button" class="wsp-x" data-wsp-dismiss aria-label="'
            + t.dismiss + '">×</button>';
    }

    function renderBanner(quiet) {
        var el = document.getElementById('weddingSitesBanner');
        if (!el) return;
        // Headline only. A host with an unspent free-site claim also gets that
        // note, because "there is one waiting for you" is worth a line where a
        // feature list is not.
        el.className = 'wsp-banner mb-3';
        el.innerHTML = thumbsHtml('wsp-thumbs', 20, 43)
            + '<span class="wsp-copy"><strong>' + copy('headline') + '</strong>'
            + (canClaim ? ' <span class="wsp-sub wsp-owned">· ' + copy('freeClaim') + '</span>' : '')
            + '</span>'
            + ctaHtml('builder_banner', 'wsp-cta')
            + closeHtml();
        el.classList.add('is-on');
        if (!quiet) track('wedding_site_pitch_view', 'builder_banner');
    }

    function renderShare(quiet) {
        var el = document.getElementById('weddingSitePitchShare');
        if (!el) return;
        // This is the considered moment — the host has a link in front of them
        // and is deciding what to send — so this surface, not the banner, is
        // where the detail goes.
        el.className = 'wsp-share';
        el.innerHTML = thumbsHtml('wsp-share-thumbs', 26, 56)
            + '<div class="wsp-share-copy"><h6>' + t.shareTitle + '</h6>'
            + '<p>' + copy('shareBody')
            + (canClaim ? ' <span class="wsp-owned">' + copy('freeClaim') + '</span>' : '')
            + '</p></div>'
            + ctaHtml('builder_share', 'wsp-cta')
            + closeHtml();
        el.classList.add('is-on');
        if (!quiet) track('wedding_site_pitch_view', 'builder_share');
    }

    function hide() {
        ['weddingSitesBanner', 'weddingSitePitchShare'].forEach(function (id) {
            var el = document.getElementById(id);
            if (el) el.classList.remove('is-on');
        });
    }

    function dismiss() {
        hide();
        try { localStorage.setItem(WSP_DISMISS_KEY, '1'); } catch (e) { /* nothing to do */ }
    }

    // One delegated listener covers both surfaces, including the copies that get
    // re-rendered when the tier answer arrives after the first paint.
    document.addEventListener('click', function (e) {
        var closer = e.target.closest && e.target.closest('[data-wsp-dismiss]');
        if (closer) {
            dismiss();
            return;
        }
        var cta = e.target.closest && e.target.closest('[data-wsp-placement]');
        if (cta) track('wedding_site_pitch_click', cta.getAttribute('data-wsp-placement'));
    });

    // Task #794: never inside a template. A template has no guests and no
    // couple yet, so "your RSVP can live inside a wedding website" is noise
    // there. Template documents have "tpl-" ids, which is known from the URL
    // before the form loads; the flag covers older flagged events, and the
    // loader calls _hideWeddingSitesPitch() for a pitch already painted.
    function inTemplate() {
        try {
            var id = new URLSearchParams(location.search).get('formId') || '';
            if (id.indexOf('tpl-') === 0) return true;
        } catch (e) { /* fall through */ }
        return !!(window.builderCore && window.builderCore.builderOptions
            && window.builderCore.builderOptions.isTemplate === true);
    }

    function render() {
        if (dismissed() || inTemplate()) return;
        kind = siteKind();
        rendered = true;
        if (kind === 'none') { hide(); return; }
        injectStyles();
        renderBanner();
        renderShare();
    }

    // The title is usually typed after the banner first paints. When it turns
    // the event into a party (or a work event), swap the wording in place; the
    // banner keeps its shape, so nothing under the host's thumb moves.
    var titleTimer = null;
    document.addEventListener('input', function (e) {
        if (!rendered || !e.target || e.target.id !== 'eventTitle') return;
        clearTimeout(titleTimer);
        titleTimer = setTimeout(function () {
            if (dismissed() || inTemplate()) return;
            var next = siteKind();
            if (next === kind) return;
            kind = next;
            if (kind === 'none') { hide(); return; }
            injectStyles();
            renderBanner(true);
            renderShare(true);
        }, 800);
    }, true);

    // Public API, unchanged for auth-manager.js. It fires once the host's tier is
    // known, which is after a Firestore round-trip — so it can land before or
    // after DOMContentLoaded depending on cache warmth.
    window._maybeShowWeddingSitesBanner = function (hasFreeSiteClaim) {
        canClaim = !!hasFreeSiteClaim;
        if (dismissed()) return;
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', render, { once: true });
        } else {
            render();
        }
    };

    window._dismissWeddingSitesBanner = dismiss;
    // Hides without recording a dismissal: leaving a template must not
    // silence the pitch on the planner's real events.
    window._hideWeddingSitesPitch = hide;

    // Exposed for the same reason the old inline script was: something else may
    // want to know whether the pitch is on screen. Cheap, and keeps `rendered`
    // from being a lint-flagged write-only variable.
    window._weddingSitesPitchShown = function () {
        return rendered && kind !== 'none' && !dismissed();
    };
})();
